import {
	DropdownMenuGroup,
	DropdownMenuPortal,
	Root as DropdownMenuRoot,
	DropdownMenuTrigger
} from '@radix-ui/react-dropdown-menu'
import type { ReactNode } from 'react'
import { Avatar } from '@/components/ui/avatar'
import {
	DropdownContent,
	DropdownItem,
	DropdownSeparator
} from '@/components/ui/dropdown-atoms'
import { cn } from '@/helpers/utils'

export interface UserMenuAction {
	label: string
	icon?: ReactNode
	onClickCb: () => void
	disabled?: boolean
}

interface UserMenuProps {
	user: {
		name: string
		email: string
		imageUrl?: string
		/**
		 * The signed-in role, already translated. There are two role
		 * vocabularies in this app, the Luminate reviewer's and the Marquee
		 * operator's, and only the caller knows which one its user carries.
		 */
		roleLabel?: string
	}
	actions: UserMenuAction[]
	sideOffset?: number
	align?: 'start' | 'center' | 'end'
	size?: 'sm' | 'md'
}

const menuSizeStyles = {
	sm: 'w-[200px]',
	md: 'w-[240px]'
}

const userInfoSizeStyles = {
	sm: 'p-1.5 gap-0.5 [&_span]:text-xs [&_small]:text-xs',
	md: 'p-2.5 gap-1 [&_span]:text-sm [&_small]:text-sm'
}

export const UserMenu = ({
	user,
	actions,
	sideOffset = 8,
	align = 'end',
	size = 'md'
}: UserMenuProps) => {
	return (
		<DropdownMenuRoot>
			<DropdownMenuTrigger asChild>
				<button
					className='inline-flex cursor-pointer items-center justify-center rounded-full border-none bg-transparent p-0 transition-opacity duration-200 hover:opacity-80 focus-visible:outline-2 focus-visible:outline-primary-500 focus-visible:outline-offset-2'
					type='button'
				>
					<Avatar
						imageUrl={user.imageUrl ?? ''}
						name={user.name ?? user.email}
						size={size}
					/>
				</button>
			</DropdownMenuTrigger>
			<DropdownMenuPortal>
				<DropdownContent
					align={align}
					className={cn(
						'z-(--z-modal) rounded-lg border border-neutral-300 bg-neutral-white py-1 shadow-lg',
						menuSizeStyles[size]
					)}
					sideOffset={sideOffset}
				>
					<div
						className={cn(
							'grid items-center',
							userInfoSizeStyles[size]
						)}
					>
						<span className='font-semibold text-neutral-700'>
							{user.name ?? user.email}
						</span>
						{user.roleLabel && (
							<small className='font-normal text-neutral-600'>
								{user.roleLabel}
							</small>
						)}
					</div>
					<DropdownSeparator />
					<DropdownMenuGroup>
						{actions.map((action) => (
							<DropdownItem
								disabled={action.disabled}
								key={action.label}
								onClick={action.onClickCb}
							>
								<div className='flex items-center gap-1.5'>
									{action.icon}
									<span>{action.label}</span>
								</div>
							</DropdownItem>
						))}
					</DropdownMenuGroup>
				</DropdownContent>
			</DropdownMenuPortal>
		</DropdownMenuRoot>
	)
}

UserMenu.displayName = 'UserMenu'
